import React, { useState } from 'react';
import { Alert, Badge, Box, Button, Container, Header, SpaceBetween, StatusIndicator } from '@cloudscape-design/components-themed/components';
import { useParams } from 'react-router-dom';
import { useProxiesHandlers } from '@/presentation/providers';
import { downloadBlob } from '@/lib/misc/utils';
import { getLocaleTimestamp } from '@/presentation/common-utils/common-utils';
import { makeNotification } from '@/presentation/pages';
import { useNotifications } from '@/presentation/providers/notifications-provider';
import { DATE_DISPLAY_FORMATS, } from '@/presentation/common-utils/constants';
import OverlayBouncyLoader from '@/presentation/components/loader/overlay-bouncy-loader';
import { ProxyRotateStrings } from '@/presentation/pages/proxies/proxies-types';
import { PagesRequestErrorHandler } from '@/presentation/pages/errors';
import { useIsMounted } from '@/presentation/hooks';
import DownloadCredentials from './proxies-download';
import { useAppContext } from '@/presentation/providers/app-context-provider';
import RotateCredentials from '@/presentation/components/rotate-credentials/credentials';
const ProxiesViewIAMAuthentication = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const params = useParams();
    const { proxies } = useProxiesHandlers();
    const proxiesHandler = proxies;
    const { pushNotifications, dismissNotification } = useNotifications();
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const onDownloadInit = () => setShowDownloadModal(true);
    const onDownloadDiscard = () => setShowDownloadModal(false);
    const [showRotateModal, setShowRotateModal] = useState(false);
    const onRotateInit = () => setShowRotateModal(true);
    const onRotateDiscard = () => setShowRotateModal(false);
    const [isOverlayLoading, setIsOverlayLoading] = useState(false);
    const isMounted = useIsMounted();
    const { isLegacyOrg } = useAppContext();
    const iamAccess = ((_b = (_a = props === null || props === void 0 ? void 0 : props.proxy) === null || _a === void 0 ? void 0 : _a.Proxies_by_pk) === null || _b === void 0 ? void 0 : _b.ProxyAwsIamAccess) || [];
    const getIamAccessArnRoles = () => {
        return iamAccess.map((item) => React.createElement(Box, { key: item.role_arn },
            React.createElement(Badge, { color: "grey", "data-dd-privacy": "mask" }, item.role_arn)));
    };
    const makeSuccessNotification = (message) => {
        const msg = 'Proxy deletion initiated successfully. It will take a short time for the proxy to be deleted.';
        return makeNotification('success', message || msg, dismissNotification);
    };
    const makeErrorNotification = (message) => {
        const msg = 'Unable to create. Please try again later.';
        return makeNotification('error', message || msg, dismissNotification);
    };
    const onDownloadConfirm = () => {
        const proxyID = Number.parseInt(params === null || params === void 0 ? void 0 : params.proxyID);
        if (isMounted.current) {
            setShowDownloadModal(false);
            setIsOverlayLoading(true);
        }
        proxiesHandler.getCredentials({ proxy_id: proxyID }).then((result) => {
            var _a;
            if (result.error) {
                const customMsg = "Unable to download proxy credentials. Please try again later.";
                pushNotifications(makeErrorNotification((result === null || result === void 0 ? void 0 : result.msg) || customMsg));
            }
            else {
                const jsonBlob = new Blob([JSON.stringify(result.data, null, 4)]);
                const proxyFileNameWithID = 'proxy-credentials-' + ((_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.client_id) + '.json';
                downloadBlob(jsonBlob, proxyFileNameWithID);
                pushNotifications(makeSuccessNotification('Proxy credentials downloaded successfully'));
            }
        }).catch(({ response }) => {
            const getResponseMessage = PagesRequestErrorHandler(response);
            pushNotifications(makeErrorNotification(getResponseMessage === null || getResponseMessage === void 0 ? void 0 : getResponseMessage.errorMessage));
        }).finally(() => {
            if (isMounted.current) {
                setIsOverlayLoading(false);
            }
        });
    };
    const onRotateConfirm = (expiry_date) => {
        const proxyID = Number.parseInt(params === null || params === void 0 ? void 0 : params.proxyID);
        if (isMounted.current) {
            setShowRotateModal(false);
            setIsOverlayLoading(true);
        }
        proxiesHandler.rotateCredentials({ proxy_id: proxyID, expiry: expiry_date }).then((result) => {
            if (result.error) {
                const customMsg = "Unable to rotate proxy credentials. Please try again later.";
                pushNotifications(makeErrorNotification((result === null || result === void 0 ? void 0 : result.msg) || customMsg));
            }
            else {
                const expiryDateTime = getLocaleTimestamp(expiry_date, DATE_DISPLAY_FORMATS.DATE_TIME);
                pushNotifications(makeSuccessNotification('Proxy credentials rotated successfully. Current credentials will expire at ' + expiryDateTime));
                setTimeout(() => {
                    onDownloadInit();
                }, 750);
            }
        }).catch(({ response }) => {
            const getResponseMessage = PagesRequestErrorHandler(response);
            pushNotifications(makeErrorNotification(getResponseMessage === null || getResponseMessage === void 0 ? void 0 : getResponseMessage.errorMessage));
        }).finally(() => {
            if (isMounted.current) {
                setIsOverlayLoading(false);
            }
        });
    };
    const credentialsConfirmationBody = (React.createElement(React.Fragment, null,
        React.createElement(Box, { variant: "span" }, "All installations of this proxy will need to be updated with the new credentials before the specified expiry time to prevent any downtime"),
        React.createElement(Alert, { type: "warning" }, "This action is irreversible and cannot be undone"),
        React.createElement(Box, null, "To avoid accidental rotations we ask you to provide additional written consent")));
    const isCredentialsAuthEnabled = ((_d = (_c = props === null || props === void 0 ? void 0 : props.proxy) === null || _c === void 0 ? void 0 : _c.Proxies_by_pk) === null || _d === void 0 ? void 0 : _d.credentials_auth_enabled) || false;
    return (React.createElement(Container, { header: React.createElement(Header, { variant: "h2", actions: React.createElement(SpaceBetween, { direction: "horizontal", size: "xs" },
                !isLegacyOrg && (React.createElement(Button, { "data-testid": "click-to-rotate-proxy-credentials", variant: "normal", onClick: onRotateInit, disabled: !isCredentialsAuthEnabled }, "Rotate Credentials")),
                React.createElement(Button, { "data-testid": "click-to-download-this-proxy", variant: "normal", onClick: onDownloadInit, disabled: !isCredentialsAuthEnabled }, "Download Credentials")) }, "Proxy Authentication") },
        React.createElement(SpaceBetween, { size: "l" },
            React.createElement(Box, null,
                React.createElement(Box, { variant: "awsui-key-label" }, "Client Credentials Auth"),
                React.createElement(Box, null, ((_f = (_e = props === null || props === void 0 ? void 0 : props.proxy) === null || _e === void 0 ? void 0 : _e.Proxies_by_pk) === null || _f === void 0 ? void 0 : _f.credentials_auth_enabled)
                    ? React.createElement(StatusIndicator, { type: "success" }, "Enabled")
                    : React.createElement(StatusIndicator, { type: "error", colorOverride: "grey" }, "Disabled"))),
            React.createElement(Box, null,
                React.createElement(Box, { variant: "awsui-key-label" }, "AWS IAM Role Auth (ARN)"),
                React.createElement(Box, null, iamAccess.length ? React.createElement(SpaceBetween, { size: "xs" }, getIamAccessArnRoles()) : '-'))),
        isOverlayLoading && React.createElement(OverlayBouncyLoader, null),
        React.createElement(DownloadCredentials, { visible: showDownloadModal, onDiscard: onDownloadDiscard, onDownload: onDownloadConfirm }),
        React.createElement(RotateCredentials, { visible: showRotateModal, onDiscard: onRotateDiscard, onRotate: onRotateConfirm, rotateModalStrings: ProxyRotateStrings, confirmationBody: credentialsConfirmationBody })));
};
export default ProxiesViewIAMAuthentication;
